import { Router } from '@angular/router';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { EnvironmentApiService } from '../core/services/environment-api.service';
import {
  GetStaffPermission,
  LoginRequest,
  LoginResponse,
  OTPResponse,
  SendEmailVerificationCodeResponse,
  VerificationOTPResponse,
} from './user.interface';
import {
  ActiveUserList,
  AddUserSubscriptionRequest,
  DirectoryRequestRequest,
  DirectoryUserResponse,
  GetUserRequest,
  UpdateApplicationUserRequest,
  UserInfo,
} from '../directory-request/directory-request.interface';
import { DirectoryAsLookupResponse } from '../directory/directory.interface';
import {
  GetFilterRolesRequest,
  UserRolesResponse,
} from '../user-roles/user-roles.interface';

@Injectable({
  providedIn: 'root',
})
export class UserService extends EnvironmentApiService {
  private storeKeyToken = 'gaif-token';
  private storeKeyLang = 'gaif-lang';
  private storeKeyPermission = 'gaif-permission';
  private storeKeyLandingPageRoute = 'gaif-landingPageRoute';
  private isLogin$ = new BehaviorSubject<boolean>(this.isLogin());
  private router = inject(Router);
  /**
   * check if user is login
   * @returns boolean
   */
  isLogin() {
    try {
      return this.getToken() ? true : false;
    } catch (error) {
      return false;
    }
  }
  /**
   *
   * @returns string return user language
   */
  getCulture() {
    let currentLang = localStorage.getItem(this.storeKeyLang);
    return currentLang ? currentLang : 'ar';
  }
  /**
   * logout user remove  user token from localStorage change isLogin$ status and router to login page
   */
  logout() {
    localStorage.removeItem(this.storeKeyToken);
    this.isLogin$.next(false);
    //this.router.navigate(['/login'])
  }
  /**
   * save token at localStorage and change isLogin$ status
   * @param token string
   */
  saveToken(token: string) {
    localStorage.setItem(this.storeKeyToken, token);
    this.isLogin$.next(true);
  }

  saveUserPermissionAndLandingPage(data: GetStaffPermission) {
    localStorage.setItem(
      this.storeKeyPermission,
      JSON.stringify(data.modulePermissionList)
    );
    localStorage.setItem(this.storeKeyLandingPageRoute, data.landingPageRoute);
  }

  getUserPermission(): number[] {
    let permission = localStorage.getItem(this.storeKeyPermission);
    return permission ? JSON.parse(permission) : [];
  }
  getUserLandingPage(): string {
    let landingPageRoute = localStorage.getItem(this.storeKeyLandingPageRoute);
    return landingPageRoute ? landingPageRoute : '/admin-template';
  }
  /**
   * Get token from localStorage
   * @returns string
   */
  getToken() {
    try {
      return localStorage.getItem(this.storeKeyToken);
    } catch (error) {
      return '';
    }
  }

  /**
   * user logout from server
   * @returns  Observable<boolean>
   */
  logoutFromServer(): Observable<boolean> {
    return this.httpClient.post<boolean>(
      `${this.baseApi}/Authenticate/Logout`,
      {}
    );
  }

  /**
   * reset user password
   * @param email string user email
   * @param newPassword string user new password
   * @returns Observable <boolean>
   */
  resetPassword(email: string, newPassword: string): Observable<boolean> {
    return this.httpClient.post<boolean>(
      `${this.baseApi}/Authenticate/ResetPassword`,
      { NewPassword: newPassword, Email: email }
    );
  }

  /**
   * user login from server
   * @param loginRequest class of LoginRequest
   * @returns Observable<LoginResponse>
   */
  login(loginRequest: LoginRequest): Observable<LoginResponse> {
    let url = `${this.baseApi}/Authenticate/login`;
    return this.httpClient.post<LoginResponse>(url, loginRequest);
  }

  /**
   *
   * @param email string
   * @param oTPId number
   * @param otp string
   * @returns Observable<SendEmailVerificationCodeResponse>
   */
  verifyEmailOTP(
    email: string,
    oTPId: number,
    otp: string
  ): Observable<VerificationOTPResponse> {
    return this.httpClient.post<VerificationOTPResponse>(
      `${this.baseApi}/OTPVerification/VerifyEmail`,
      { OTPVerificationId: oTPId, OTPCode: otp, email: email }
    );
  }

  /**
   * send reset password OTP
   * @param email string
   * @returns  Observable<OTPResponse>
   */
  sendResetPasswordOtp(email: string): Observable<OTPResponse> {
    return this.httpClient.post<OTPResponse>(
      `${this.baseApi}/OTPVerification/SendEmailVerificationCode`,
      { email: email }
    );
  }

  getDirectoryActiveWebsiteUsers(directoryRequest: DirectoryRequestRequest) {
    return this.httpClient.post<ActiveUserList>(
      `${this.baseApi}/Authenticate/GetActiveWebsiteUsers`,
      directoryRequest
    );
  }

  getUserSubscriptions(userId: number) {
    return this.httpClient.post<DirectoryUserResponse>(
      `${this.baseApi}/Authenticate/GetUserSubscriptions/?userId=${userId}`,
      {}
    );
  }

  addUserSubscription(addUserSubscriptionRequest: AddUserSubscriptionRequest) {
    return this.httpClient.post<boolean>(
      `${this.baseApi}/Authenticate/AddUserSubscription`,
      addUserSubscriptionRequest
    );
  }

  activateUser(request: GetUserRequest) {
    return this.httpClient.post<boolean>(
      `${this.baseApi}/Authenticate/ActivateUser`,
      request
    );
  }

  deactivateUser(request: GetUserRequest) {
    return this.httpClient.post<boolean>(
      `${this.baseApi}/Authenticate/DeactivateUser`,
      request
    );
  }

  getUserSubscriptionWithAllDirectories(email: string) {
    return this.httpClient.post<DirectoryAsLookupResponse[]>(
      `${this.baseApi}/Authenticate/GetUserSubscriptionWithAllDirectories`,
      { Email: email }
    );
  }

  getUserInfoById(userId: number) {
    return this.httpClient.get<UserInfo>(
      `${this.baseApi}/Authenticate/GetUserInfoById/?userId=${userId}`
    );
  }

  deleteUser(request: GetUserRequest) {
    return this.httpClient.post<boolean>(
      `${this.baseApi}/Authenticate/DeleteUser`,
      request
    );
  }

  updateUserInfo(request: UpdateApplicationUserRequest) {
    return this.httpClient.post<boolean>(
      `${this.baseApi}/Authenticate/UpdateUserInfo`,
      request
    );
  }

  getFilteredRoles(request: GetFilterRolesRequest) {
    return this.httpClient.post<UserRolesResponse>(
      `${this.baseApi}/Authenticate/GetFilteredRoles`,
      request
    );
  }

  deleteRole(roleId: number) {
    return this.httpClient.post<{
      isSuccess: boolean;
      errorId: number;
      error: string;
    }>(`${this.baseApi}/Authenticate/DeleteRole?roleId=${roleId}`, {});
  }

  getStaffPermission() {
    return this.httpClient.get<GetStaffPermission>(
      `${this.baseApi}/Authenticate/GetStaffPermission`
    );
  }
}
